import { Spin, Typography } from 'antd';
import { FC } from 'react';

import styles from './styles.module.scss';

import podiumBg1st from '../../../../../assets/images/1st-blank-sm.png';
import podiumBg2nd from '../../../../../assets/images/2nd-blank-sm.png';
import podiumBg3rd from '../../../../../assets/images/3rd-blank-sm.png';
import lowerBg from '../../../../../assets/images/lower-leaderboard-bg.png';
import { IRecruiterDashboardSettings } from '../../../hooks/useRecruiterDashboardSettings';
import {
  LeaderboardPeriod,
  IRecruiterLeaderboardEntry,
} from '../../../hooks/useRecruiterLeaderboard';
import { getInitials, getUserColor } from '../../../utils/avatarUtils';

const { Text } = Typography;

// ── Colours ──────────────────────────────────────────────
const CARD_BG = '#0e4d8f';
const NUMBER_COLOR = '#cedbef';

const NAME_COLORS: Record<number, string> = {
  1: '#f1af27',
  2: '#2bb4f7',
  3: '#f36118',
};

// Podium card background images per rank (include wreath + rank + gradient)
const PODIUM_BG: Record<number, string> = {
  1: podiumBg1st,
  2: podiumBg2nd,
  3: podiumBg3rd,
};

// ── Sub-components ───────────────────────────────────────

/** Period toggle button group */
const PeriodToggle: FC<{
  period: LeaderboardPeriod;
  onChange: (p: LeaderboardPeriod) => void;
}> = ({ period, onChange }) => {
  const options: { value: LeaderboardPeriod; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'history', label: 'History' },
  ];

  return (
    <div className={styles.periodToggle}>
      {options.map((opt) => (
        <button
          key={opt.value}
          className={`${styles.periodButton} ${
            period === opt.value ? styles.active : ''
          }`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

/** Avatar circle for list rows */
const AvatarCircle: FC<{
  userId: string;
  firstName: string;
  lastName: string;
  avatarBase64?: string;
  defaultAvatarBase64?: string | null;
  size?: number;
}> = ({
  userId,
  firstName,
  lastName,
  avatarBase64,
  defaultAvatarBase64,
  size = 36,
}) => {
  const src = avatarBase64 || defaultAvatarBase64 || undefined;

  if (src) {
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          // border: '2px solid rgba(255,255,255,0.08)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: getUserColor(userId),
        fontSize: size * 0.38,
        fontWeight: 600,
        color: '#fff',
        flexShrink: 0,
        border: '2px solid rgba(255,255,255,0.08)',
      }}
    >
      {getInitials(firstName, lastName)}
    </div>
  );
};

/** Top-3 podium item */
const PodiumItem: FC<{
  entry: IRecruiterLeaderboardEntry;
  rank: number;
  avatarBase64?: string;
  defaultAvatarBase64?: string | null;
  onEntryClick?: (entry: IRecruiterLeaderboardEntry) => void;
}> = ({ entry, rank, avatarBase64, defaultAvatarBase64, onEntryClick }) => {
  const avatarSrc = avatarBase64 || defaultAvatarBase64 || undefined;
  const bgImg = PODIUM_BG[rank] || PODIUM_BG[3];

  // avatar sizing: slightly bigger for 1st
  const avatarSize = rank === 1 ? 75 : 60;

  return (
    <div
      className={styles.podiumItem}
      style={{ order: rank === 1 ? 0 : rank === 2 ? -1 : 1 }}
    >
      <div className={styles.podiumCard}>
        <div
          className={styles.podiumCardInner}
          style={{
            backgroundImage: `url(${bgImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Avatar positioned in the lower portion of the card */}
          <div className={styles.podiumAvatar}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={`${entry.firstName} ${entry.lastName}`}
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  objectFit: 'cover',
                  background: 'transparent',
                }}
              />
            ) : (
              <div
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: getUserColor(entry.userId),
                  fontSize: avatarSize * 0.38,
                  fontWeight: 600,
                  color: '#fff',
                  border: '2px solid rgba(255,255,255,0.15)',
                }}
              >
                {getInitials(entry.firstName, entry.lastName)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer: name + count */}
      <div className={styles.podiumFooter}>
        <div className={styles.podiumName} style={{ color: NAME_COLORS[rank] }}>
          {entry.firstName}
        </div>
        <div className={styles.podiumName} style={{ color: NAME_COLORS[rank] }}>
          {entry.lastName}
        </div>
        <div
          className={styles.podiumCount}
          style={{ cursor: onEntryClick ? 'pointer' : undefined }}
          onClick={(e) => {
            if (onEntryClick) {
              e.stopPropagation();
              onEntryClick(entry);
            }
          }}
        >
          <span>🐩</span>{' '}
          <span style={{ color: NAME_COLORS[rank] }}>
            {entry.newDonorCount.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

/** List row for positions 4+ (wreath is baked into lower bg image) */
const ListRow: FC<{
  entry: IRecruiterLeaderboardEntry;
  avatarBase64?: string;
  defaultAvatarBase64?: string | null;
  onEntryClick?: (entry: IRecruiterLeaderboardEntry) => void;
}> = ({ entry, avatarBase64, defaultAvatarBase64, onEntryClick }) => (
  <div className={styles.listRow}>
    <div className={styles.listAvatar}>
      <AvatarCircle
        userId={entry.userId}
        firstName={entry.firstName}
        lastName={entry.lastName}
        avatarBase64={avatarBase64}
        defaultAvatarBase64={defaultAvatarBase64}
        size={42}
      />
    </div>
    <div className={styles.listInfo}>
      <span className={styles.listName}>
        {entry.firstName} {entry.lastName}
      </span>
      <span
        className={styles.listCount}
        style={{ cursor: onEntryClick ? 'pointer' : undefined }}
        onClick={(e) => {
          if (onEntryClick) {
            e.stopPropagation();
            onEntryClick(entry);
          }
        }}
      >
        🐩{' '}
        <span style={{ color: NUMBER_COLOR }}>
          {entry.newDonorCount.toLocaleString()}
        </span>
      </span>
    </div>
  </div>
);

// ── Dummy preview data ──────────────────────────────────
const DUMMY_ENTRIES: IRecruiterLeaderboardEntry[] = [
  {
    userId: 'dummy-4',
    firstName: 'COL',
    lastName: 'Recruiter',
    newDonorCount: 0,
  },
  {
    userId: 'dummy-5',
    firstName: 'DFW',
    lastName: 'Recruiter',
    newDonorCount: 0,
  },
  { userId: 'dummy-6', firstName: 'TBD', lastName: '', newDonorCount: 0 },
];

// ── Main Component ───────────────────────────────────────

export interface ILeaderboardHistoryWeek {
  weekNumber: number;
  year: number;
  weekEndDate: string;
  winnerName?: string;
  winnerRegion?: string;
  winnerUserId?: string;
  recruitedCount: number;
}

export interface IRecruitersLeaderboardCardProps {
  period: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
  entries: IRecruiterLeaderboardEntry[];
  loading: boolean;
  settings: IRecruiterDashboardSettings | null;
  onEntryClick?: (entry: IRecruiterLeaderboardEntry) => void;
  historyWeeks?: ILeaderboardHistoryWeek[];
  historyLoading?: boolean;
}

export const RecruitersLeaderboardCard: FC<IRecruitersLeaderboardCardProps> = ({
  period,
  onPeriodChange,
  entries,
  loading,
  settings,
  onEntryClick,
  historyWeeks,
  historyLoading,
}) => {
  const avatars = settings?.avatars ?? {};
  const defaultAvatarBase64 = settings?.defaultAvatarBase64 ?? null;

  // Top 3: only real recruiters (empty slots if < 3)
  const top3: (IRecruiterLeaderboardEntry | null)[] = [
    entries[0] ?? null,
    entries[1] ?? null,
    entries[2] ?? null,
  ];
  // 4th–6th: real recruiters first, pad with dummies to fill 3 slots
  const realRest = entries.slice(3, 6);
  const rest =
    realRest.length >= 3
      ? realRest
      : [...realRest, ...DUMMY_ENTRIES.slice(realRest.length, 3)];

  return (
    <div className={styles.leaderboardCard} style={{ background: CARD_BG }}>
      {/* Header */}
      <div className={styles.header}>
        <Text strong style={{ color: '#fff', fontSize: 16 }}>
          Recruiters&apos; Leaderboard
        </Text>
        <PeriodToggle period={period} onChange={onPeriodChange} />
      </div>

      {/* Content */}
      {period === 'history' ? (
        historyLoading ? (
          <div className={styles.centered}>
            <Spin />
          </div>
        ) : (
          <div
            style={{
              maxHeight: 500,
              overflowY: 'auto',
              padding: '0 8px',
            }}
          >
            {(historyWeeks ?? []).map((week) => {
              const sundayDate = new Date(week.weekEndDate);
              const dateLabel = sundayDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              });
              return (
                <div
                  key={`${week.year}-${week.weekNumber}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      color: '#cedbef',
                      fontWeight: 600,
                      minWidth: 52,
                    }}
                  >
                    Week {week.weekNumber}
                  </span>
                  <span
                    style={{ color: 'rgba(255,255,255,0.4)', minWidth: 55 }}
                  >
                    {dateLabel}
                  </span>
                  <span
                    style={{
                      color: '#fff',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {week.winnerName
                      ? `${week.winnerName}${
                          week.winnerRegion ? ` (${week.winnerRegion})` : ''
                        }`
                      : '—'}
                  </span>
                  <span
                    style={{
                      color:
                        week.recruitedCount > 0
                          ? '#f1af27'
                          : 'rgba(255,255,255,0.3)',
                      fontWeight: 700,
                      minWidth: 24,
                      textAlign: 'right',
                    }}
                  >
                    {week.recruitedCount}
                  </span>
                </div>
              );
            })}
            {(!historyWeeks || historyWeeks.length === 0) && (
              <div
                style={{
                  color: 'rgba(255,255,255,0.4)',
                  textAlign: 'center',
                  padding: 24,
                }}
              >
                No history data
              </div>
            )}
          </div>
        )
      ) : loading ? (
        <div className={styles.centered}>
          <Spin />
        </div>
      ) : (
        <div className={styles.rankingsWrapper}>
          <div className={styles.leaderboardWidget}>
            {/* Top 3 Podium */}
            <div className={styles.podium}>
              {top3.map((entry, i) =>
                entry ? (
                  <PodiumItem
                    key={entry.userId}
                    entry={entry}
                    rank={i + 1}
                    avatarBase64={avatars[entry.userId]}
                    defaultAvatarBase64={defaultAvatarBase64}
                    onEntryClick={onEntryClick}
                  />
                ) : (
                  <div
                    key={`empty-${i}`}
                    className={styles.podiumItem}
                    style={{ order: i + 1 === 1 ? 0 : i + 1 === 2 ? -1 : 1 }}
                  />
                )
              )}
            </div>

            {/* Lower section */}
            <div
              className={styles.lowerSection}
              style={{
                backgroundImage: `url(${lowerBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {rest.map((entry) => (
                <ListRow
                  key={entry.userId}
                  entry={entry}
                  avatarBase64={avatars[entry.userId]}
                  defaultAvatarBase64={defaultAvatarBase64}
                  onEntryClick={
                    entry.userId.startsWith('dummy-') ? undefined : onEntryClick
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
